import React, { Fragment } from "react";
import { string, arrayOf, func, bool, shape } from "prop-types";
import TableRowActions from "./table-row-actions";
import Button from "../Button/Button";

const Table = ({
  data = [],
  columns = [],
  actions = [],
  size = "medium",
  hasNextPage = false,
  hasPrevPage = false,
  onPrevPage = () => {},
  onNextPage = () => {},
  paged = false,
  onRowClick = () => {},
  isRowClickable = false,
  hideActionsOnMobile = false,
  sortConfig = { key: null, direction: "asc" },
  onSortChange,
  stickyHeader = false,
}) => {
  const filterActions = (actions, row) => {
    return actions.filter((a) => {
      return !a.shouldShow || a.shouldShow(row);
    });
  };

  const onRowClickHandler = (rowData) => {
    if (!window.getSelection().toString()) {
      onRowClick(rowData);
    }
  };

  const rowRenderHandler = (field, render, row) => {
    return render ? render(row) : row[field];
  };

  const getClassNamesFor = (name) => {
    if (!sortConfig) return;
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  if (data.length === 0) {
    return <div className="table-wrapper__empty">No Data to display</div>;
  }

  return (
    <Fragment>
      <div className="table-wrapper">
        <table className="table-wrapper__table">
          <thead
            className={`table-wrapper__table__head ${
              stickyHeader ? "table-wrapper__table__head--sticky" : ""
            }`}
          >
            <tr>
              {columns.map(
                (
                  {
                    title,
                    secondTitle,
                    textAlign,
                    showOnMobile,
                    hideMobileUp,
                    field,
                    sortable,
                    className,
                  },
                  colIndex
                ) => (
                  <th
                    className={`table-wrapper__table__cell ${
                      textAlign
                        ? `table-wrapper__table__cell--${textAlign}`
                        : ""
                    } ${
                      showOnMobile
                        ? ""
                        : "table-wrapper__table__cell--hide-on-mobile"
                    }
                    ${
                      hideMobileUp
                        ? "table-wrapper__table__cell--hide-mobile-up"
                        : ""
                    }
                    ${getClassNamesFor(field)} ${className || ""}`}
                    key={colIndex}
                    onClick={sortable ? () => onSortChange(field) : undefined}
                    style={{ cursor: sortable ? "pointer" : "default" }}
                  >
                    <div className="table-header-rows">
                      <div>
                        {title}
                        <br />
                        {secondTitle}
                      </div>
                      {sortable && (
                        <span className="sort-icon">
                          {sortConfig.key === field ? (
                            sortConfig.direction === "asc" ? (
                              <ArrowUp />
                            ) : (
                              <ArrowDown />
                            )
                          ) : (
                            <ArrowUp />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                )
              )}
              {actions && actions.length > 0 && (
                <th className="table-wrapper__table__cell">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr
                className={
                  isRowClickable
                    ? "table-wrapper table-wrapper__table-row--clickable"
                    : "table-wrapper table-wrapper__table-data"
                }
                key={`table-wrapper__table-row-${rowIndex}`}
                onClick={() => onRowClickHandler(row)}
              >
                {columns.map(
                  (
                    {
                      field,
                      render,
                      textAlign,
                      verticalAlign,
                      showOnMobile,
                      hideMobileUp,
                      className,
                    },
                    index
                  ) => (
                    <td
                      valign={verticalAlign || "top"}
                      className={`table-wrapper__table__cell ${
                        textAlign
                          ? `table-wrapper__table__cell--${textAlign}`
                          : ""
                      } ${
                        row.error ? "table-wrapper__table__cell--error" : ""
                      } ${
                        showOnMobile
                          ? ""
                          : "table-wrapper__table__cell--hide-on-mobile"
                      }
                      ${
                        hideMobileUp
                          ? "table-wrapper__table__cell--hide-mobile-up"
                          : ""
                      } ${className || ""}`}
                      key={index}
                    >
                      {rowRenderHandler(field, render, row)}
                    </td>
                  )
                )}
                {actions && actions.length > 0 && (
                  <td
                    className={`table-wrapper__table__cell table-wrapper__table__cell--actions ${
                      hideActionsOnMobile
                        ? "table-wrapper__table__cell--hide-on-mobile"
                        : ""
                    }`}
                  >
                    <TableRowActions
                      isRowClickable={isRowClickable}
                      actions={filterActions(actions, row)}
                      rowData={row}
                      size={size}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(hasPrevPage || hasNextPage) && (
        <div
          className={`table-wrapper__paging-container ${
            paged ? "" : "table-wrapper__paging-container--hidden"
          }`}
        >
          <Button
            className="button--outlined button--fixed-width table-wrapper__paging-container__previous"
            onClick={onPrevPage}
            name="Previous"
            disabled={!hasPrevPage}
          />
          <Button
            className="button--primary button--fixed-width"
            onClick={onNextPage}
            name="Next"
            disabled={!hasNextPage}
          />
        </div>
      )}
    </Fragment>
  );
};

const ArrowUp = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    className="bi bi-arrow-up"
    viewBox="0 0 16 16"
  >
    <path
      fillRule="evenodd"
      d="M8 12a.5.5 0 0 0 .5-.5V4.707l3.15 3.15a.5.5 0 1 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 4.707V11.5A.5.5 0 0 0 8 12z"
    />
  </svg>
);

const ArrowDown = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="currentColor"
    className="bi bi-arrow-down"
    viewBox="0 0 16 16"
  >
    <path
      fillRule="evenodd"
      d="M8 4a.5.5 0 0 1 .5.5v6.793l3.15-3.15a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 1 1 .708-.708L7.5 11.293V4.5A.5.5 0 0 1 8 4z"
    />
  </svg>
);

Table.propTypes = {
  data: arrayOf(shape({})).isRequired,
  size: string,
  columns: arrayOf(
    shape({
      title: string.isRequired,
      secondTitle: string,
      field: string.isRequired,
      render: func,
      textAlign: string,
      verticalAlign: string,
      showOnMobile: bool,
      hideMobileUp: bool,
      sortable: bool,
    })
  ).isRequired,
  onRowSelect: func,
  actions: arrayOf(
    shape({
      name: string.isRequired,
      onClick: func.isRequired,
      shouldShow: func,
    })
  ),
  hasNextPage: bool,
  hasPrevPage: bool,
  onNextPage: func,
  onPrevPage: func,
  paged: bool,
  onRowClick: func,
  isRowClickable: bool,
  hideActionsOnMobile: bool,
  sortConfig: shape({
    key: string,
    direction: string,
  }),
  onSortChange: func.isRequired,
  stickyHeader: bool,
};

export default Table;
